/*
 * Client-side pagination for the collection grid.
 *
 * The section renders every product in one request with in-stock products first and
 * sold-out products last. This element then chunks the list into pages, so out-of-stock
 * items always appear at the bottom of the last page(s) rather than on every page.
 */

// If a stale ?page= param is present (e.g. an old bookmark or external link), the server
// would have paginated by 250 and returned an empty page. Strip it and reload once so the
// full product list is fetched and paginated on the client instead.
(function stripStalePageParam() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('page')) {
      params.delete('page');
      const query = params.toString();
      window.location.replace(window.location.pathname + (query ? '?' + query : '') + window.location.hash);
    }
  } catch (e) {
    /* no-op */
  }
})();

const SO_CARET_SVG =
  '<span class="svg-wrapper"><svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg></span>';

class SoldOutPagination extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('#product-grid');
    this.nav = this.querySelector('[data-so-pagination]');
    if (!this.grid || !this.nav) return;

    this.perPage = parseInt(this.dataset.perPage, 10) || 16;
    this.items = Array.from(this.grid.children).filter((el) => el.tagName === 'LI');
    this.totalPages = Math.max(1, Math.ceil(this.items.length / this.perPage));
    this.currentPage = 1;

    this.nav.addEventListener('click', this.onNavClick.bind(this));

    if (this.totalPages <= 1) {
      this.nav.hidden = true;
      this.items.forEach((item) => this.showItem(item, true));
      return;
    }

    this.render();
  }

  onNavClick(event) {
    const control = event.target.closest('[data-so-page]');
    if (!control) return;
    event.preventDefault();
    const page = parseInt(control.dataset.soPage, 10);
    if (!Number.isNaN(page)) this.goToPage(page);
  }

  goToPage(page) {
    const target = Math.min(Math.max(page, 1), this.totalPages);
    if (target === this.currentPage) return;
    this.currentPage = target;
    this.render();
    this.scrollToTop();
  }

  scrollToTop() {
    const top = this.getBoundingClientRect().top + window.pageYOffset - 100;
    window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
  }

  showItem(item, visible) {
    item.style.display = visible ? '' : 'none';
    item.toggleAttribute('hidden', !visible);
  }

  render() {
    const start = (this.currentPage - 1) * this.perPage;
    const end = start + this.perPage;
    this.items.forEach((item, index) => {
      this.showItem(item, index >= start && index < end);
    });
    this.renderNav();
  }

  buildParts() {
    const total = this.totalPages;
    const current = this.currentPage;
    const parts = [];
    const add = (page) => {
      if (!parts.includes(page)) parts.push(page);
    };

    add(1);
    for (let page = current - 1; page <= current + 1; page += 1) {
      if (page > 1 && page < total) add(page);
    }
    add(total);
    parts.sort((a, b) => a - b);

    const withGaps = [];
    parts.forEach((page, index) => {
      if (index > 0 && page - parts[index - 1] > 1) withGaps.push('gap');
      withGaps.push(page);
    });
    return withGaps;
  }

  renderNav() {
    const current = this.currentPage;
    const total = this.totalPages;
    let html = '<nav class="pagination" role="navigation" aria-label="Pagination"><ul class="pagination__list list-unstyled" role="list">';

    if (current > 1) {
      html += `<li><button type="button" data-so-page="${current - 1}" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce so-pagination__prev" aria-label="Previous page">${SO_CARET_SVG}</button></li>`;
    }

    this.buildParts().forEach((part) => {
      if (part === 'gap') {
        html += '<li><span class="pagination__item">&hellip;</span></li>';
      } else if (part === current) {
        html += `<li><a role="link" aria-disabled="true" aria-current="page" class="pagination__item pagination__item--current light">${part}</a></li>`;
      } else {
        html += `<li><button type="button" data-so-page="${part}" class="pagination__item link" aria-label="Page ${part}">${part}</button></li>`;
      }
    });

    if (current < total) {
      html += `<li><button type="button" data-so-page="${current + 1}" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce so-pagination__next" aria-label="Next page">${SO_CARET_SVG}</button></li>`;
    }

    html += '</ul></nav>';
    this.nav.innerHTML = html;
    this.nav.hidden = false;
    this.nav.dataset.page = current;
  }
}

if (!customElements.get('sold-out-pagination')) {
  customElements.define('sold-out-pagination', SoldOutPagination);
}
